/**
 * Typed client for /api/scheduler/*.
 *
 * The shapes here mirror what the API's mergeTrigger() returns. Keep them in
 * step: a field dropped on the server does not fail a build here (api/ and src/
 * are separate TypeScript projects), it fails at runtime in the browser.
 */
import { apiFetch } from '../api'

export type IntervalUnit = 'minutes' | 'hours' | 'days'

export type Schedule =
  | { type: 'interval'; interval: { every: number; unit: IntervalUnit } }
  | { type: 'time-of-day'; times: string[]; days?: string[] }
  | { type: 'on-event'; event: string; eventLabel?: string }

export type JobStatus = 'success' | 'failed' | 'running' | 'skipped'

export interface LastRun {
  at: string
  status: JobStatus
  message: string
  durationMs: number
}

export interface ConfigControl {
  key: string
  type: 'toggle' | 'number' | 'text' | 'select' | 'recipients'
  label: string
  help?: string
  value: unknown
  min?: number
  max?: number
  step?: number
  unit?: string
  options?: Array<{ value: string; label: string }>
}

export interface Impact {
  severity: 'high' | 'medium' | 'low'
  audience: string
  systems: string[]
  notes?: string
}

export interface ChangelogEntry {
  v: string
  at: string
  note: string
}

export interface SchedulerApp {
  key: string
  name: string
  blurb: string
}

export interface Trigger {
  id: string
  app: string
  name: string
  enabled: boolean
  runnable: boolean
  action: string
  cadence: string
  schedule: Schedule
  scheduleSummary: string
  nextRun: string
  managed: 'external' | null
  summary: string
  description: string
  does: string
  config: ConfigControl[]
  impact: Impact
  version: string
  timeoutSec: number
  updatedAt: string
  updatedBy: string
  changelog: ChangelogEntry[]
  lastRun: LastRun | null
  seeded: boolean
}

export interface TriggersResponse {
  apps: SchedulerApp[]
  triggers: Trigger[]
}

export interface HistoryEntry {
  at: string
  status: JobStatus
  message: string
  durationMs: number
}

export function listTriggers(): Promise<TriggersResponse> {
  return apiFetch<TriggersResponse>('/api/scheduler/triggers')
}

export function patchTrigger(
  id: string,
  body: { enabled?: boolean; schedule?: Schedule; config?: Record<string, unknown> },
): Promise<{ trigger: Trigger }> {
  return apiFetch<{ trigger: Trigger }>(`/api/scheduler/triggers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export function runTriggerNow(id: string): Promise<{ id: string; lastRun: LastRun }> {
  return apiFetch<{ id: string; lastRun: LastRun }>(
    `/api/scheduler/triggers/${encodeURIComponent(id)}/run`,
    { method: 'POST' },
  )
}

export function getHistory(id: string): Promise<{ id: string; history: HistoryEntry[] }> {
  return apiFetch<{ id: string; history: HistoryEntry[] }>(
    `/api/scheduler/triggers/${encodeURIComponent(id)}/history`,
  )
}

// ─── display helpers ─────────────────────────────────────────────────────────

/** "just now" / "14 min ago" / "3 Aug 07:00" for a UTC ISO timestamp. */
export function relativeTime(iso: string): string {
  if (!iso) return '—'
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return '—'
  const mins = Math.floor((Date.now() - then) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(then).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDuration(ms: number): string {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export const DAY_LABELS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
]
